import { MmSkuKey } from '../types/domain';

const KEY_SEPARATOR = '::';

/** null и undefined дают пустую часть ключа: это корректное значение, а не отсутствие данных */
function part(value: number | string | null | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }

  return escapePart(String(value));
}

function escapePart(value: string): string {
  return value.split('\\').join('\\\\').split(KEY_SEPARATOR).join('\\:\\:');
}

export function buildSkuKeyString(key: MmSkuKey): string {
  return [
    part(key.storeFormatId),
    part(key.storeRegionId),
    part(key.storeClusterId),
    part(key.groupId),
    part(key.categoryId),
    part(key.subcategoryId),
    part(key.internalCode),
  ].join(KEY_SEPARATOR);
}

export function extractSkuKey(row: MmSkuKey): MmSkuKey {
  return {
    storeFormatId: row.storeFormatId,
    storeRegionId: row.storeRegionId === undefined ? null : row.storeRegionId,
    storeClusterId: row.storeClusterId === undefined ? null : row.storeClusterId,
    groupId: row.groupId,
    categoryId: row.categoryId,
    subcategoryId: row.subcategoryId === undefined ? null : row.subcategoryId,
    internalCode: row.internalCode,
  };
}

export function skuKeyOf(row: MmSkuKey): string {
  return buildSkuKeyString(extractSkuKey(row));
}

export function parseSkuKeyString(raw: string): MmSkuKey | null {
  const parts = splitEscaped(raw);

  if (parts.length !== 7) {
    return null;
  }

  const storeFormatId = toNumberOrNull(parts[0]);
  const groupId = toNumberOrNull(parts[3]);
  const categoryId = toNumberOrNull(parts[4]);

  if (storeFormatId === null || groupId === null || categoryId === null || parts[6] === '') {
    return null;
  }

  return {
    storeFormatId: storeFormatId,
    storeRegionId: toNumberOrNull(parts[1]),
    storeClusterId: toNumberOrNull(parts[2]),
    groupId: groupId,
    categoryId: categoryId,
    subcategoryId: toNumberOrNull(parts[5]),
    internalCode: parts[6],
  };
}

function toNumberOrNull(raw: string): number | null {
  if (raw === '') {
    return null;
  }

  const parsed = Number(raw);

  return isNaN(parsed) ? null : parsed;
}

function splitEscaped(raw: string): string[] {
  const result: string[] = [];
  let current = '';
  let index = 0;

  while (index < raw.length) {
    if (raw.charAt(index) === '\\' && index + 1 < raw.length) {
      const next = raw.charAt(index + 1);

      if (next === '\\') {
        current += '\\';
        index += 2;
        continue;
      }

      if (next === ':') {
        current += ':';
        index += 2;
        continue;
      }
    }

    if (raw.substr(index, KEY_SEPARATOR.length) === KEY_SEPARATOR) {
      result.push(current);
      current = '';
      index += KEY_SEPARATOR.length;
      continue;
    }

    current += raw.charAt(index);
    index += 1;
  }

  result.push(current);

  return result;
}

export function isSameSkuKey(a: MmSkuKey, b: MmSkuKey): boolean {
  return buildSkuKeyString(a) === buildSkuKeyString(b);
}

export function hasCompleteSkuKey(row: Partial<MmSkuKey> | null | undefined): boolean {
  if (!row) {
    return false;
  }

  const hasFormat = typeof row.storeFormatId === 'number';
  const hasGroup = typeof row.groupId === 'number';
  const hasCategory = typeof row.categoryId === 'number';
  const hasCode = typeof row.internalCode === 'string' && row.internalCode !== '';

  // Эти поля допускают null, поэтому проверяем наличие ключа, а не значение
  const hasRegion = 'storeRegionId' in row;
  const hasCluster = 'storeClusterId' in row;
  const hasSubcategory = 'subcategoryId' in row;

  return hasFormat && hasGroup && hasCategory && hasCode && hasRegion && hasCluster && hasSubcategory;
}
